import AdBanner from "@/components/AdBanner.tsx";
import AdRails from "@/components/AdRails.tsx";
import {config} from "@/config/config.ts";
import {scrollToTop} from "@/utilities/utilities.ts";
import {Icon} from "@iconify/react";
import {Link} from "@tanstack/react-router";
import {ReactNode} from "react";
import footerLogo from '../assets/footer_logo.png';

function FooterLink({children, to}: { children: ReactNode, to: string }) {
    return <Link
        onClick={() => scrollToTop()}
        to={to}
        className="hover:text-[#FDE61E] transition text-base"
    >
        {children}
    </Link>
}

/** `ads={false}` drops both ad units: the home page carries none (see AppThemed AD_FREE_PATHS). */
const FooterSection = ({ads = true}: {ads?: boolean}) => {
    return (
        <>
        {/* Ad rails for every public marketing/tool sub-page. Fixed to the viewport gutters and only
            shown on very wide screens (>=1850px), so page-tree position here is purely for reuse. */}
        {ads && <AdRails slot={config.adsenseContentSlot} labelClassName="text-black/30" />}
        {/* Full-width responsive banner shown at ALL screen sizes (the rails above are >=1850px
            only), so phone/laptop visitors on every public page get an ad, not just ultra-wide. */}
        <section className="bg-[#EDEEF4] pt-8">
            {ads && <AdBanner className="pb-6" />}
            <div className="bg-[#2A2C2E] rounded-t-3xl px-12 py-8">
                <div className="flex items-center justify-between max-lg:flex-col max-lg:gap-6">
                    <div className="flex-shrink-0">
                        <img src={footerLogo} alt="ScorePal" className="h-12" />
                    </div>

                    <nav className="flex items-center gap-[7vw] font-inter max-md:flex-col max-md:gap-4 text-white">
                        <FooterLink to="/privacy/">Privacy Policy</FooterLink>
                        <FooterLink to="/cookie-policy/">Cookie Policy</FooterLink>
                        <FooterLink to="/terms/">Terms</FooterLink>
                        <FooterLink to="/disclaimer/">Disclaimer</FooterLink>
                    </nav>
                </div>

                <div className="flex items-center justify-between max-lg:flex-col max-lg:gap-6 mt-4 pt-4 border-t-1 border-white/40">
                    <div className={"w-full grid grid-cols-5 max-lg:grid-cols-2 max-md:grid-cols-1 gap-4 font-inter text-white/40"}>
                        <nav className="flex flex-col max-md:gap-2 items-center">
                            <span className={"text-white font-semibold"}>Features</span>

                            <FooterLink to="/find-tournaments/">Find Tournaments</FooterLink>
                            <FooterLink to="/club-directory/">Club Directory</FooterLink>
                            <FooterLink to="/sports-analytics-software-for-clubs/">Sports Analytics</FooterLink>
                            <FooterLink to="/sports-tournament-management-software/">Tournament Management</FooterLink>
                            <FooterLink to="/sports-league-management-software/">League Management</FooterLink>
                            <FooterLink to="/live-scoring/">Live Scoring App</FooterLink>
                            <FooterLink to="/for-clubs/">For Clubs</FooterLink>
                            <FooterLink to="/for-communities/">For Communities</FooterLink>
                        </nav>

                        <nav className="flex flex-col max-md:gap-2 items-center">
                            <span className={"text-white font-semibold"}>Free Tools</span>

                            <FooterLink to="/tournament-formats/">Tournament Formats</FooterLink>
                            <FooterLink to="/league-formats/">League Formats</FooterLink>
                            <FooterLink to="/round-robin-tournament-generator/">Round Robin Generator</FooterLink>
                            <FooterLink to="/single-elimination-bracket-generator/">Single Elimination</FooterLink>
                            <FooterLink to="/double-elimination-bracket-generator/">Double Elimination</FooterLink>
                            <FooterLink to="/americano-generator/">Americano Generator</FooterLink>
                            <FooterLink to="/mexicano-tournament-generator/">Mexicano Generator</FooterLink>
                            <FooterLink to="/swiss-system-tournament-generator/">Swiss System</FooterLink>
                            <FooterLink to="/ladder-league-generator/">Ladder League</FooterLink>
                            <FooterLink to="/king-of-the-court-generator/">King of the Court</FooterLink>
                            <FooterLink to="/group-and-knockout-generator/">Group &amp; Knockout</FooterLink>
                            <FooterLink to="/create-brackets/">Bracket Generator</FooterLink>
                        </nav>

                        <nav className="flex flex-col max-md:gap-2 items-center">
                            <span className={"text-white font-semibold"}>Sports Tournaments</span>

                            <FooterLink to="/padel-tournament-software/">Padel Tournament Software</FooterLink>
                            <FooterLink to="/pickleball-tournament-software/">Pickleball Tournament Software</FooterLink>
                            <FooterLink to="/tennis-tournament-software/">Tennis Tournament Software</FooterLink>
                            <FooterLink to="/badminton-tournament-software/">Badminton Tournament Software</FooterLink>
                            <FooterLink to="/squash-tournament-software/">Squash Tournament Software</FooterLink>
                            <FooterLink to="/basketball-tournament-software/">Basketball Tournament Software</FooterLink>
                            <FooterLink to="/football-tournament-software/">Football Tournament Software</FooterLink>
                            <FooterLink to="/esports-tournament-software/">Esports Tournament Software</FooterLink>
                            <FooterLink to="/volleyball-tournament-software/">Volleyball Tournament Software</FooterLink>
                            <FooterLink to="/roundnet-tournament-software/">Roundnet Tournament Software</FooterLink>
                        </nav>

                        <nav className="flex flex-col max-md:gap-2 items-center">
                            <span className={"text-white font-semibold"}>Sports Leagues</span>

                            <FooterLink to="/padel-league-software/">Padel League Software</FooterLink>
                            <FooterLink to="/pickleball-league-software/">Pickleball League Software</FooterLink>
                            <FooterLink to="/tennis-league-software/">Tennis League Software</FooterLink>
                            <FooterLink to="/squash-league-software/">Squash League Software</FooterLink>
                            <FooterLink to="/football-league-software/">Football League Software</FooterLink>
                            <FooterLink to="/basketball-league-software/">Basketball League Software</FooterLink>
                            <FooterLink to="/volleyball-league-software/">Volleyball League Software</FooterLink>
                            <FooterLink to="/roundnet-league-software/">Roundnet League Software</FooterLink>
                            <FooterLink to="/esports-league-software/">Esports League Software</FooterLink>
                        </nav>

                        <nav className="flex flex-col max-md:gap-2 items-center">
                            <span className={"text-white font-semibold"}>About</span>

                            <FooterLink to="/pricing/">Pricing</FooterLink>
                            <FooterLink to="/contact/">Contact</FooterLink>
                            <FooterLink to="/about/">About</FooterLink>
                            <FooterLink to="/documentation/">Help Center</FooterLink>
                        </nav>

                    </div>
                </div>

                <div className="flex justify-end items-center gap-4 text-2xl mt-6 pt-4 border-t border-white/40">
                    <a href="https://www.instagram.com/scorepal_hq/" target="_blank" rel="noopener noreferrer"
                       aria-label="ScorePal on Instagram" className="text-white hover:text-[#FDE61E] transition">
                        <Icon icon="mdi:instagram" />
                    </a>
                    <a href="https://www.facebook.com/people/Score-Pal/61575217685230/" target="_blank" rel="noopener noreferrer"
                       aria-label="ScorePal on Facebook" className="text-white hover:text-[#FDE61E] transition">
                        <Icon icon="mdi:facebook" />
                    </a>
                </div>
            </div>
        </section>
        </>
    );
};

export default FooterSection;
